import { NextRequest, NextResponse } from 'next/server';
import { db } from '@/lib/db';
import bcrypt from 'bcryptjs';

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();

    // Validate required fields
    const {
      firstName,
      businessName,
      email,
      password,
      phone,
      location,
      aboutGym,
      photos,
      services,
      membershipDescription,
      operatingHours,
      facilityFeatures,
    } = body;

    // Validation
    if (!firstName || !businessName || !email || !password || !phone || !location) {
      return NextResponse.json(
        { error: 'Missing required fields: firstName, businessName, email, password, phone, location' },
        { status: 400 }
      );
    }

    // Password validation
    if (password.length < 8) {
      return NextResponse.json(
        { error: 'Password must be at least 8 characters' },
        { status: 400 }
      );
    }

    if (!aboutGym || aboutGym.trim().length < 50) {
      return NextResponse.json(
        { error: 'About gym must be at least 50 characters' },
        { status: 400 }
      );
    }

    if (!photos || photos.length === 0) {
      return NextResponse.json(
        { error: 'At least one photo is required' },
        { status: 400 }
      );
    }

    if (!services || services.length === 0) {
      return NextResponse.json(
        { error: 'At least one service must be selected' },
        { status: 400 }
      );
    }

    if (!membershipDescription || membershipDescription.trim().length < 20) {
      return NextResponse.json(
        { error: 'Membership description must be at least 20 characters' },
        { status: 400 }
      );
    }

    if (!operatingHours) {
      return NextResponse.json(
        { error: 'Operating hours are required' },
        { status: 400 }
      );
    }

    if (!facilityFeatures) {
      return NextResponse.json(
        { error: 'Facility features are required' },
        { status: 400 }
      );
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return NextResponse.json(
        { error: 'Invalid email format' },
        { status: 400 }
      );
    }

    // Check if user already exists
    const existingUser = await db.users.findByEmail(email);
    if (existingUser) {
      return NextResponse.json(
        { error: 'Email already registered' },
        { status: 400 }
      );
    }

    // Hash password
    const passwordHash = await bcrypt.hash(password, 10);

    // Create user account
    await db.users.create({
      fullName: firstName,
      email,
      phone,
      passwordHash,
      role: 'gym',
      status: 'active'
    });

    // Create gym in database
    const gym = await db.gyms.create({
      ownerEmail: email,
      firstName,
      businessName: body.businessName || null,
      phone,
      location,
      aboutGym,
      photos,
      services,
      membershipPricing: body.membershipPricing || null,
      membershipDescription,
      operatingHours,
      facilityFeatures,
      status: 'pending', // Default status is pending approval
    });

    // TODO: Send confirmation email to gym owner
    // TODO: Send notification email to admin

    return NextResponse.json({
      success: true,
      message: 'Gym registered successfully. Awaiting admin approval.',
      gym: {
        id: gym.id,
        businessName: gym.businessName || `${gym.firstName}'s Gym`,
        status: gym.status,
        createdAt: gym.createdAt,
      },
    });
  } catch (error) {
    console.error('Gym registration error:', error);
    
    // Detailed error for debugging
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error details:', errorMessage);
    
    return NextResponse.json(
      { 
        error: 'Failed to register gym. Please try again.',
        details: process.env.NODE_ENV === 'development' ? errorMessage : undefined
      },
      { status: 500 }
    );
  }
}
